/**
 * provider.ts — the SINGLE api surface for the doctor-only (provider) WEB app.
 *
 * ────────────────────────────────────────────────────────────────────────
 * RESOLVED CONTRACT (see billing/docs/architecture-plan/work/PROVIDER_WEB_CONTRACT.md,
 * which extends work/PROVIDER_APP_CONTRACT.md). The doctor-web list + detail
 * are served by a SINGLE, server-scoped provider endpoint each:
 *   • list   → GET /revenue/provider/cases            (§2 — id+time-only, redacted)
 *   • detail → GET /revenue/provider/cases/{id}        (§3 — ENRICHED bakeoff
 *              MINUS coder: the row + html_refs evidence links + the bucketed
 *              editable billing_data / line_preview the editor needs)
 * The dx-match / write / queue paths are the EXISTING bakeoff endpoints, already
 * provider-gated server-side (§4–§5).
 * ────────────────────────────────────────────────────────────────────────
 * The backend enforces per-provider isolation SERVER-SIDE — provider identity
 * is resolved from the JWT (`User.provider_key`), never sent by the client. A
 * client-supplied `?provider=` is ignored/forbidden (mismatch → 403); reading
 * another provider's encounter by id → 404 (ids are not probeable).
 *
 * DOCTOR-WEB SCOPE POLICY (enforced SERVER-SIDE per §1): the list shows ONLY
 * the doctor's own encounters, ONLY unbilled (`is_upbill_eligible` gate), ONLY
 * the past `PROVIDER_WINDOW_DAYS` (= 14) days. The backend already redacts the
 * list to id+time (`_provider_redact_case`); we project it to the typed
 * `ProviderWorklistRow` so the UI literally cannot render a redacted field.
 * The detail view is the biller's bakeoff surface MINUS the coder-vs-coder
 * comparison (the backend drops the coder columns via
 * `_provider_detail_drop_coder`), ENRICHED with the note/billing evidence
 * links (`html_refs`) + the bucketed editable line set (`billing_data` /
 * `line_preview`) so the provider app fetches that ONE endpoint for detail —
 * it no longer reads the review (`/revenue/cases/{id}`) or billing-payload
 * (`/revenue/cases/{id}/billing-payload`) surfaces.
 *
 * SCOPING GUARANTEE: the app passes NO client-side provider param on ANY
 * request. The dedicated list endpoint has no provider/identity param at all;
 * scoping is purely server-side. (Seam test asserts no `provider` is sent.)
 *
 * ADAPTER SEAM: `toWorklistRow` is the ONE localized spot that maps the
 * contract's redacted list row into the doctor-web view row. The 14-day +
 * unbilled filtering is now done server-side by the dedicated endpoint, so the
 * client no longer derives it; `isUnbilledEncounter` remains as a small helper
 * over the contract's `is_unbilled` flag (defaulting true, since the endpoint
 * only ever returns unbilled rows by construction).
 */

import apiClient from "@/lib/api/client";
import {
  setDxPointers as revenueSetDxPointers,
  writeSingleClaimToAthena,
  queueClaimForAthena,
  persistClaimEdit,
  fetchDxMatch,
  type RuCase,
  type RuCaseHtmlRefs,
  type RuBillingData,
  type RuBillingPreviewRow,
  type RuSetDxPointersResult,
  type RuDxMatch,
  type RuBakeoffQueueLine,
  type RuAthenaWriteSingleResponse,
  type RuAssessmentPlan,
  type RuLineWarning,
  type RuLineWarningSeverity,
  type RuValidateResult,
  type RuEraAdjudication,
} from "@/lib/api/revenue";

// ── Seeded demo provider logins (DEV/DEMO seed — not production secrets) ────
// The login screen offers each of these as a one-tap, localhost-only demo
// sign-in. Each entry is a `provider`-role user bound server-side to a distinct
// `provider_key`; the JWT carries the scope, so the app NEVER sees or sends the
// key (per-provider isolation is forced server-side — see permissions.py
// `require_provider`). The whole provider story is generic over the
// authenticated user, so adding a provider is a CONFIG-ONLY change: seed the
// user (backend/app/seed/seed_users.py) and append one entry here.
//
// IMPORTANT: `email`/`password` must be the credentials that actually work in
// live Postgres (`app.users`). The seed file (backend/app/seed/seed_users.py)
// and this list now agree on the ONE working Chotani login,
// `hchotani@cninternalmedicine.com` — the old `chotani@healthhub.de.gov` was a
// red herring (non-existent / cannot log in) and has been removed from the seed.
// Always wire the EMAIL THAT EXISTS in `app.users`.
//
// 2026-08-03 credential reset: Chotani and Saba Sheikh keep their existing
// cninternalmedicine.com / practice2.com addresses (only passwords rotated).
// The provider_admin ("Basmaa") and provider-QA-sandbox accounts moved to
// @oneriver.ai along with every other non-provider login; see seed_users.py
// for the full rationale (local-part collisions disambiguated with a
// `.<practice>` / `.sandbox` suffix).
export interface ProviderDemoLogin {
  email: string;
  password: string;
  /** Button label, e.g. "Provider (Dr. Chotani)". */
  label: string;
  /** Practice slug for context only (null/_global for hub providers). */
  practiceSlug?: string | null;
  /**
   * True for the provider-ADMIN super-user ("Dr. Basmaa") logins — a per-practice
   * super-user (role `provider_admin`) who acts as ANY provider in her practice
   * via the sub-account dropdown, unlike a normal provider locked to one key.
   */
  providerAdmin?: boolean;
}

export const PROVIDER_DEMO_LOGINS: ReadonlyArray<ProviderDemoLogin> = [
  // Dr. Habib Chotani — bound to provider_key PMG_CNIM_Chotani_H, org
  // "Delaware Rural Health Hub" (practice_slug NULL => "_global").
  {
    email: "hchotani@cninternalmedicine.com",
    password: "8zie^!p63Qkh92*X",
    label: "Provider (Dr. Chotani)",
    practiceSlug: null,
  },
  // Dr. Saba Sheikh — internal-testing provider, bound to provider_key
  // Saba_Sheikh, org "Practice 2 Clinic" (practice_slug "practice2").
  {
    email: "saba.sheikh@practice2.com",
    password: "Yx7$Kq2!mZ9@vRt5#Lb",
    label: "Provider (Dr. Sheikh · Practice 2)",
    practiceSlug: "practice2",
  },
  // Dr. Basmaa — provider-ADMIN super-user, TWO accounts (one per practice so
  // tenant isolation is never crossed). Each acts as ANY provider IN THAT
  // PRACTICE via the sub-account dropdown; working the other practice means
  // logging into the other account. Role `provider_admin` (see permissions.py).
  {
    email: "basmaa.hub@oneriver.ai",
    password: "!MCm7mTfm#$F5B2X",
    label: "Provider Admin (Dr. Basmaa · Health Hub)",
    practiceSlug: null,
    providerAdmin: true,
  },
  {
    email: "basmaa.practice2@oneriver.ai",
    password: "@oQ&QLk41yESnj0*",
    label: "Provider Admin (Dr. Basmaa · Practice 2)",
    practiceSlug: "practice2",
    providerAdmin: true,
  },
  // Dr. Basmaa "plan 3" — the THIRD provider-admin account, bound to Columbia
  // Medical Practice (practice_slug "practice3", Athena practiceId 30148). Acts
  // as any rendering provider within practice3 via the sub-account dropdown.
  {
    email: "basmaa.practice3@oneriver.ai",
    password: "!f7ag@tFvMrJ!S8U",
    label: "Provider Admin (Dr. Basmaa · Practice 3)",
    practiceSlug: "practice3",
    providerAdmin: true,
  },
  // Provider-QA SANDBOX doctor — reuses the ENTIRE provider flow but is a
  // re-runnable sandbox: reads the REAL 8042 schedule (same closed encounters
  // Dr. Chotani sees), publishes coded results into the ISOLATED `qa_sandbox`
  // namespace, and REDIRECTS every write-out to the practice test appointment
  // (473347569) so it never touches a real patient claim. Role `provider_qa`
  // (+`provider` for gating), org "QA Sandbox Clinic" (practice_slug
  // "qa_sandbox"). See seed_users.py / config.py.
  {
    email: "qa.sandbox@oneriver.ai",
    password: "6W#yrab%!MNxFkWi",
    label: "Provider QA (Sandbox)",
    practiceSlug: "qa_sandbox",
  },
];

/**
 * Back-compat: the FIRST provider demo login (Dr. Chotani). Prefer
 * `PROVIDER_DEMO_LOGINS` (the full registry) for anything new — the login grid
 * is generated from the list so adding a provider needs no UI edit.
 */
export const PROVIDER_DEMO_LOGIN = PROVIDER_DEMO_LOGINS[0];

// ── Provider-admin sub-account selector (client-side scope threading) ───────
// A normal provider is locked to their own key server-side and sends NO
// provider param. A provider_admin (Dr. Basmaa), however, ACTS AS a chosen
// provider within her practice — the dropdown in the provider layout sets the
// selected key, and the axios request interceptor threads it as `?provider=`
// on EVERY provider-scoped request (see client.ts + providerScope.ts). The
// backend validates the selection against the providers that exist in HER
// practice (out-of-practice keys → 403), so this is scope SELECTION, never
// widening. When null (a normal provider, or "all" not chosen) no param is
// sent and the server falls back to the caller's own scope.
//
// The selection STATE lives in ./providerScope so the interceptor can read it
// without importing this module (which would create a circular import). We
// re-export the setter/getter here so the dropdown's existing imports from
// "@/lib/api/provider" keep working.
export {
  setSelectedProviderKey,
  getSelectedProviderKey,
} from "./providerScope";
import { getSelectedProviderKey as _getSelectedProviderKey } from "./providerScope";

/**
 * Merge the selected provider key into an axios params object as `provider=`.
 * Retained for the few call sites that build an explicit params object; the
 * request interceptor also appends it globally, so new call sites don't need
 * this. A no-op for a normal provider (nothing selected).
 */
function withProviderParam<T extends Record<string, unknown> | undefined>(
  params?: T,
): Record<string, unknown> | undefined {
  const key = _getSelectedProviderKey();
  if (!key) return params;
  return { ...(params ?? {}), provider: key };
}

/**
 * Endpoint registry — the single wiring surface. Components import the typed
 * functions below, never these strings, so this object is the only place an
 * endpoint path changes. All paths are relative to the `/api` base (applied by
 * apiClient). The list + detail are the DEDICATED, server-scoped provider
 * endpoints (PROVIDER_WEB_CONTRACT §2–§3); dx-match / write / queue reuse the
 * existing bakeoff endpoints, which are provider-gated server-side (§4–§5).
 */
export const PROVIDER_ENDPOINTS = {
  /** List MY encounters — server forces own·unbilled·14d + id+time redaction. */
  listMyEncounters: "/revenue/provider/cases",
  /** Provider-only: the single highest-priority unworked encounter. */
  nextCase: "/revenue/provider/next-case",
  /**
   * The SINGLE provider detail endpoint (PROVIDER_WEB_CONTRACT §3): one
   * encounter as the bakeoff ROW MINUS the coder columns, ENRICHED with
   * `html_refs` (note/billing evidence links) + the bucketed `billing_data`
   * / `line_preview` the `ProviderLineEditor` consumes. Server-scoped: no
   * provider param, 404 on cross-provider access. This is the ONLY detail
   * source the provider app uses — the review (`/revenue/cases/{id}`) and
   * billing-payload (`/revenue/cases/{id}/billing-payload`) surfaces are no
   * longer fetched by the doctor-web app.
   */
  getEncounter: (id: string) => `/revenue/provider/cases/${encodeURIComponent(id)}`,
  /** CPT -> ICD-10 best-match + the per-charge dx-pointer table. */
  dxMatch: (id: string) =>
    `/revenue/bakeoff/dx-match/encounter/${encodeURIComponent(id)}`,
  /** Durable dx-pointer write (set-pointers human event). */
  setPointers: (id: string) =>
    `/revenue/bakeoff/dx-match/encounter/${encodeURIComponent(id)}/set-pointers`,
  /** Write / submit the provider's finished edits now. */
  writeOut: (id: string) =>
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(id)}/write-single`,
  /** Park the provider's edits on the day's write queue. */
  queue: (id: string) =>
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(id)}/queue`,
  /**
   * Auto-save the provider's edited charge set onto the durable overlay
   * (no RPA / no write-queue row). Called on every add/delete/modifier/units
   * change so edits survive reload and feed write-out. Provider-gated.
   */
  persistEdit: (id: string) =>
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(id)}/persist-edit`,
  /**
   * Billing-ONLY write ("Write" button) — enter the current in-page codes on
   * the slip WITHOUT creating a claim. The backend ADDS the codes via POST
   * /billing (rich per-line: modifiers + per-line dx preserved) then LOCKS them
   * via POST /enter-charges ("Save & Enter Charges"); NO claim. Returns the
   * enter-charges ``{jobId,...}`` to poll via {@link PROVIDER_ENDPOINTS.writeStatus}.
   */
  write: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/write`,
  /**
   * Full write + validate + create claim ("Write and Submit" button). Proxies
   * POST /write-and-submit to the RPA and returns its ``{jobId, ...}`` (or the
   * duplicate ``{status:"in_progress"|"already_queued", jobId:null}`` body).
   */
  writeAndSubmit: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/write-and-submit`,
  preflightAdvisory: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/preflight-advisory`,
  /** G8 §8.2 — does this outgoing payload require expert approval? */
  approvalEvaluate: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/approval-evaluate`,
  /** G8 §9.1 — record an expert decision (approve/reject/changes) for a payload. */
  approvalDecision: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/approval-decision`,
  /** Poll a write-and-submit job to completion (passthrough of the RPA status). */
  writeAndSubmitStatus: (jobId: string) =>
    `/revenue/provider/write-and-submit/status/${encodeURIComponent(jobId)}`,
  /**
   * Poll a billing-only "Write" job to completion. "Write" returns an
   * ENTER-CHARGES (lock) jobId, polled via this dedicated route (backed by
   * GET /enter-charges/:jobId), NOT the write-and-submit status route.
   */
  writeStatus: (jobId: string) =>
    `/revenue/provider/write/status/${encodeURIComponent(jobId)}`,
  /**
   * Provider "Verify" — advisory existence + claim-correctness on the CURRENT
   * line set WITHOUT writing. Provider-scoped (own encounter only; 404 on
   * cross-provider). Returns the {@link RuValidateResult} warning map keyed by
   * code (CPT/HCPCS or ICD-10 string). Advisory only — never blocks/writes.
   */
  validateLines: (appointmentId: string) =>
    `/revenue/provider/encounter/${encodeURIComponent(appointmentId)}/validate-lines`,
} as const;

// ── Types re-exported for the provider app (thin aliases) ────────────────
// Keeping provider-facing names lets the UI read cleanly and gives us a seam
// to remap fields if the contract response shapes ever differ.
export type ProviderEncounter = RuCase;
export type ProviderDxMatch = RuDxMatch;
export type ProviderQueueLine = RuBakeoffQueueLine;
/** Structured Assessment & Plan evidence (same shape the bakeoff detail uses). */
export type ProviderAssessmentPlan = RuAssessmentPlan;

// The "Verify" advisory warning shapes are shared with the biller validate-lines
// contract (single source of truth in revenue.ts) — re-exported so the provider
// app imports them from its one api seam.
export type { RuLineWarning, RuLineWarningSeverity, RuValidateResult };

/**
 * Per-charge "why this code" rationale (PROVIDER_WEB_CONTRACT §3). Mirrors the
 * per-line evidence the bakeoff detail attaches to each proposed line — the
 * rule that fired, the AMA/CMS citation, the matched note span, and the chart
 * section — projected to a coder-free shape. One entry per (cpt, instance) so
 * repeated multi-billable CPTs line up with the editor rows in appearance order.
 */
export interface ProviderChargeJustification {
  cpt: string;
  /**
   * Durable per-line identity (BE-06A) so the FE can attach this evidence to
   * the EXACT physical line — duplicate CPTs carry DISTINCT keys. Additive/
   * optional: null on legacy payloads (fall back to the (cpt, instance_idx)
   * pair below). `instance_idx` is now display-only (appearance order).
   */
  stable_line_key?: string | null;
  instance_idx: number;
  cpt_description: string | null;
  /** Friendly category label ("E&M Lift", "Missed Procedure", …). */
  kind_label: string | null;
  category: string | null;
  /** The rule that proposed this code (the "why" predicate id). */
  rule_path: string | null;
  /** The matched note text / chart section that supports the code. */
  evidence_span: string | null;
  evidence_section: string | null;
  /** AMA/CMS authority — short reason + manual citation + reference url. */
  citation: {
    short_reason: string | null;
    ama_short: string | null;
    ama_url: string | null;
  } | null;
}

/**
 * The enriched single-endpoint detail payload (PROVIDER_WEB_CONTRACT §3). The
 * backend returns the bakeoff ROW MINUS the coder columns at the TOP LEVEL
 * (flat), plus the enrichment blocks. The seam adapter (`getMyEncounter`)
 * normalizes the flat row into a `case` object so the detail UI keeps reading
 * `detail.case.*` exactly as it did off the old review payload — the coder
 * comparison fields are simply never present.
 */
export interface ProviderEncounterDetail {
  /** The bakeoff-minus-coder encounter row, normalized for the detail UI. */
  case: Partial<RuCase> & {
    encounter_id: string;
    claim_id: string | null;
    patient_id: string | null;
    dos_iso: string | null;
    payer: string | null;
    payer_bucket: string | null;
    /**
     * Appointment wall-clock datetime (date + time) — the SAME signal the
     * provider list card shows (corpus appt time, falling back to date-only
     * dos_iso). The detail header formats this with `fmtEncounterTime`.
     */
    appointment_iso?: string | null;
  };
  /** Note/billing evidence links (only present when the raw HTML exists). */
  html_refs?: RuCaseHtmlRefs;
  /** Bucketed editable line set the ProviderLineEditor consumes. */
  billing_data: RuBillingData;
  /** Per-line preview rows (descriptions the editor reads). */
  line_preview: RuBillingPreviewRow[];
  /**
   * Per-charge "why this code" rationale — one entry per (cpt, instance),
   * keyed by appearance order so it lines up with the editor rows. Reuses the
   * SAME per-line evidence the bakeoff detail attaches. May be empty.
   */
  charge_justifications: ProviderChargeJustification[];
  /**
   * Structured Assessment & Plan evidence (the clinical source supporting the
   * bill), parsed from the encounter chart — same shape the bakeoff detail
   * surfaces. `available=false` when no chart is on file (render gracefully).
   */
  assessment_plan?: ProviderAssessmentPlan;

  /**
   * BE-05: source-freshness + run/snapshot IDs on the served encounter payload.
   * All additive/optional (absent on legacy payloads). Derived from the EXISTING
   * serving freshness primitives — NO DOS-based inference.
   *
   * `source_changed_since_coding` is the ONLY staleness signal the cockpit
   * should surface (null when the live source can't be resolved — never guess).
   */
  /** ISO time the CURRENT live source projection was built (null if unknown). */
  source_snapshot_at?: string | null;
  /** ISO time the snapshot this encounter was CODED against was built. */
  coded_against_snapshot_at?: string | null;
  /**
   * True iff the underlying source changed since this encounter was coded
   * (different live run, or the live projection is newer). null when the live
   * source can't be resolved. The ONLY staleness signal — no DOS inference.
   */
  source_changed_since_coding?: boolean | null;
  /** The run id this encounter was coded in (optional). */
  coding_run_id?: string | null;
  /** The current live source snapshot/run id (optional). */
  source_snapshot_id?: string | null;
  /** The snapshot/run id this encounter was coded against (optional). */
  coded_against_snapshot_id?: string | null;

  /**
   * BE-07: lines the engine considered but did NOT put on the primary bill —
   * ``candidate_lines`` (also-considered, biller-triage) and ``suppressed_lines``
   * ("why not" — the engine dropped them). Each carries a reason +
   * stable_line_key. Additive/optional: absent/empty when the case has none or
   * the live CaseArtifact can't be read (legacy/synthesized encounters).
   */
  candidate_lines?: RuAltLine[];
  suppressed_lines?: RuAltLine[];
}

/**
 * A non-primary line the engine considered (BE-07): a candidate (also-considered)
 * or suppressed ("why not") line, projected from the CaseArtifact. Carries the
 * durable stable_line_key so the FE can bind evidence/state to it.
 */
export interface RuAltLine {
  cpt: string;
  cpt_description?: string | null;
  stable_line_key?: string | null;
  /** Which bucket this line represents. */
  recommendation_state: "candidate" | "suppressed";
  /** Engine tier ('candidate' | 'dropped' | 'primary') when known. */
  tier?: string | null;
  /** Engine decision ('recommend'|'upbill'|'suppress'|'candidate') when known. */
  decision?: string | null;
  /** Best-effort human reason (engine decision + rule ids that fired). */
  reason?: string | null;
  expected_allowed?: number;
}

/**
 * The doctor-facing worklist row — the ONLY fields the scoped web list view is
 * allowed to render. Matches the redaction the existing bakeoff/biller surfaces
 * apply: a patient *id* (opaque) + the encounter *time* (DOS), plus minimal
 * case/encounter identifiers and an unbilled indicator. NO names / DOB / payer
 * detail / dollars / other PHI. This is deliberately a SUBSET of RuCase so the
 * list component literally cannot show a redacted field — the type forbids it.
 */
export interface ProviderWorklistRow {
  encounter_id: string;
  case_id: string | null;
  patient_id: string | null;
  /** Encounter time / date of service (ISO when available). */
  dos_iso: string | null;
  /**
   * Appointment wall-clock datetime (date + time) when the source carries it,
   * else the date-only DOS, else null. ISO ("YYYY-MM-DDTHH:MM:00" with time,
   * "YYYY-MM-DD" without). The list renders date + time from this.
   */
  appointment_iso: string | null;
  /** Still open to bill (the past-2-weeks unbilled worklist). */
  unbilled: boolean;
  /**
   * Durably written-out to Athena (billed). When true the card shows the
   * Billed pill instead of Unbilled — the encounter has a restart-durable
   * write-out / queued-for-Athena marker on the backend. Defaults false.
   */
  billed: boolean;
  /**
   * Coded but not yet written out — the MIDDLE stage of the provider billing
   * story (Unbilled → Coded → Billed). True when the coding pipeline has
   * finished for this encounter (durable backend `is_coded` marker) but it has
   * not yet been billed. A billed encounter is never also coded. Defaults false.
   */
  coded: boolean;
  /**
   * Submitted to payer — the TERMINAL stage of the provider billing story
   * (Unbilled → Coded → Billed → Submitted). True when Athena has generated the
   * claim (durable backend `is_submitted` marker). A submitted encounter is
   * always also billed. Defaults false.
   */
  submitted: boolean;
  /**
   * Not Closed — the stage BEFORE coding (visit exists / is happening but the
   * provider has not yet signed off the encounter). True when the daily sweep
   * materialised a card for this appointment and it has not advanced to coded /
   * billed yet. Defaults false. The sign-off poller flips it to coded on close.
   */
  not_closed?: boolean;
  /**
   * Live Athena billing status fused from the appointments API (the "new API"
   * the doctor wants driving open-vs-billed): "submitted" = a claim has been
   * generated / sent to the payer (terminal), "billed" = charges entered on the
   * slip / claim already created, "coded" = our pipeline coded it and it awaits
   * write-out, "not_closed" = visit exists, provider not yet signed off (the
   * pre-coding stage), null = no live signal for this appointment. NOTE: there
   * is NO "ready_to_bill" value anymore — the auto-bill subsystem that produced
   * it (dashboard "Enter charges" / checked-out) was removed; a merely checked-
   * out but not-signed-off appointment is simply not_closed / null, never ready.
   */
  billing_status?:
    | "billed"
    | "coded"
    | "submitted"
    | "not_closed"
    | null;
  /**
   * Reconciliation-status fields (ghost / split-sibling / critical-miss). These
   * are an artifact-status ADVISORY, not PHI — the doctor needs to know "verify
   * this in Athena" before working a ghost/sibling encounter — so they are
   * allowed on the redacted worklist row. All optional: when the backend omits
   * them the shared <ReconciliationBadge/> simply hides (graceful degrade).
   */
  has_critical_miss?: boolean;
  has_split_sibling?: boolean;
  has_ghost_unresolved?: boolean;
  is_ghost_sibling?: boolean;
  ghost_resolved?: boolean;
  internal_miss_allowed?: number;
  sibling_claim_ids?: string[];
}

export interface ProviderEncounterList {
  total: number;
  encounters: ProviderWorklistRow[];
  /** The server's 14-day window (echoed from the contract list response). */
  windowDays: number;
  /** Inclusive lower bound on `dos_iso` the server applied (YYYY-MM-DD). */
  windowCutoffIso: string | null;
  /** Still-unbilled count from the shared digest worklist service. Not HTTP total. */
  unbilledCount?: number;
  sourceFreshnessState?: string;
  coverageState?: string;
}

// ── Scope policy (the 14-day / unbilled rule) ──────────────────────────────
// Per PROVIDER_WEB_CONTRACT §1, the doctor scope (own · unbilled · past 14
// days) is enforced ENTIRELY server-side by the dedicated list endpoint. This
// constant mirrors the backend's `PROVIDER_WINDOW_DAYS` for display only; the
// client does NOT compute or send the window.
export const PROVIDER_LIST_WINDOW_DAYS = 14;

/**
 * The raw contract row from `GET /revenue/provider/cases` (§2). Already
 * REDACTED server-side to id + time (no name/DOB/PHI), with a few cheap dollar
 * headlines + the `is_unbilled` gate echoed back. We deliberately project it
 * down to `ProviderWorklistRow` (id+time only) in `toWorklistRow`, so the UI
 * never even receives the headline dollars in its render path.
 */
interface ProviderCaseRow {
  encounter_id: string;
  claim_id?: string | null;
  patient_id?: string | null;
  dos_iso?: string | null;
  /** Appointment wall-clock datetime (date + time) or date-only fallback. */
  appointment_iso?: string | null;
  is_unbilled?: boolean;
  /** Durably written-out to Athena (billed). See ProviderWorklistRow.billed. */
  is_billed?: boolean;
  /** Coded-but-not-billed (middle stage). See ProviderWorklistRow.coded. */
  is_coded?: boolean;
  /** Submitted to payer (terminal stage). See ProviderWorklistRow.submitted. */
  is_submitted?: boolean;
  /** Not-closed (pre-coding stage). See ProviderWorklistRow.not_closed. */
  is_not_closed?: boolean;
  /** Live Athena status fused from the appointments API. See ProviderWorklistRow. */
  billing_status?:
    | "billed"
    | "coded"
    | "submitted"
    | "not_closed"
    | null;
  /** Reconciliation-status advisory fields (not PHI). See ProviderWorklistRow. */
  has_critical_miss?: boolean;
  has_split_sibling?: boolean;
  has_ghost_unresolved?: boolean;
  is_ghost_sibling?: boolean;
  ghost_resolved?: boolean;
  internal_miss_allowed?: number;
  sibling_claim_ids?: string[];
}

interface ProviderCasesResponse {
  total?: number;
  window_days?: number;
  window_cutoff_iso?: string | null;
  cases?: ProviderCaseRow[];
  unbilled_count?: number;
  source_freshness_state?: string;
  coverage_state?: string;
  unbilled_evaluated_at?: string;
}

/**
 * Unbilled flag for a contract list row. The dedicated endpoint only ever
 * returns unbilled rows (it applies the `is_upbill_eligible` gate server-side),
 * echoing `is_unbilled` per row; we default to `true` when omitted. THIS is the
 * one spot to adjust if the contract's unbilled signal ever changes.
 */
export function isUnbilledEncounter(row: { is_unbilled?: boolean }): boolean {
  return row.is_unbilled ?? true;
}

/**
 * Billed flag for a contract list row. True when the encounter has a durable
 * written-out / queued-for-Athena marker (`is_billed`), so the card shows the
 * Billed pill. Defaults false (the common unbilled case).
 */
export function isBilledEncounter(row: { is_billed?: boolean }): boolean {
  return row.is_billed ?? false;
}

/**
 * Coded flag for a contract list row. True when the coding pipeline has finished
 * for this encounter (`is_coded`) but it has not yet been written out — the
 * middle "Coded" stage. Defaults false.
 */
export function isCodedEncounter(row: { is_coded?: boolean }): boolean {
  return row.is_coded ?? false;
}

/**
 * Submitted flag for a contract list row. True when Athena has generated the
 * claim (`is_submitted`) — the terminal stage, a strict upgrade over billed.
 * Defaults false.
 */
export function isSubmittedEncounter(row: { is_submitted?: boolean }): boolean {
  return row.is_submitted ?? false;
}

/**
 * The single mapping spot (localized adapter). Projects the contract's redacted
 * list row down to the `ProviderWorklistRow` the doctor list renders (id+time +
 * unbilled flag only). Adapting to a changed contract row is a change to THIS
 * function only — components never touch the raw row.
 */
export function toWorklistRow(c: ProviderCaseRow): ProviderWorklistRow {
  // Prefer the LIVE Athena status (the appointments API) when present: a
  // "billed" live status means a claim already exists in Athena even if we
  // hold no local write-out marker. Otherwise fall back to the durable
  // written-out flag.
  const liveBilled = c.billing_status === "billed";
  const submitted =
    c.billing_status === "submitted" || isSubmittedEncounter(c);
  const billed = submitted || liveBilled || isBilledEncounter(c);
  // Coded = pipeline finished, not yet billed. Billed always wins over coded.
  const coded = !billed && (c.billing_status === "coded" || isCodedEncounter(c));
  // Not Closed = visit exists, provider not yet signed off (the pre-coding
  // stage). Ranks below coded/billed — never shown as Not-Closed once advanced.
  const notClosed =
    !billed && !coded &&
    (c.billing_status === "not_closed" || (c.is_not_closed ?? false));
  const row: ProviderWorklistRow = {
    encounter_id: c.encounter_id,
    case_id: c.claim_id ?? null,
    patient_id: c.patient_id ?? null,
    dos_iso: c.dos_iso ?? null,
    appointment_iso: c.appointment_iso ?? c.dos_iso ?? null,
    // A billed/submitted (written-out or live-billed) OR coded OR not-closed
    // encounter is not "unbilled".
    unbilled: billed || coded || notClosed ? false : isUnbilledEncounter(c),
    billed,
    coded,
    submitted,
    not_closed: notClosed,
    billing_status: c.billing_status ?? null,
  };
  // Reconciliation-status advisory (NOT PHI) — pass through ONLY the fields the
  // server actually sent so the redacted row stays id+time tidy when there's no
  // reconciliation signal. The shared <ReconciliationBadge/> reads these to
  // render "Verify in Athena" / "Reconciled" etc. on the doctor worklist; when
  // they're all absent the badge simply hides (graceful degrade).
  if (c.has_critical_miss !== undefined) row.has_critical_miss = c.has_critical_miss;
  if (c.has_split_sibling !== undefined) row.has_split_sibling = c.has_split_sibling;
  if (c.has_ghost_unresolved !== undefined) row.has_ghost_unresolved = c.has_ghost_unresolved;
  if (c.is_ghost_sibling !== undefined) row.is_ghost_sibling = c.is_ghost_sibling;
  if (c.ghost_resolved !== undefined) row.ghost_resolved = c.ghost_resolved;
  if (c.internal_miss_allowed !== undefined) row.internal_miss_allowed = c.internal_miss_allowed;
  if (c.sibling_claim_ids !== undefined) row.sibling_claim_ids = c.sibling_claim_ids;
  return row;
}

// ── List MY encounters (scoped: own · unbilled · past 2 weeks) ─────────────
/**
 * The provider's own encounter worklist via the DEDICATED, server-scoped
 * endpoint (PROVIDER_WEB_CONTRACT §2). The app passes NO provider param — the
 * endpoint has no identity param at all; the server forces own·unbilled·14d
 * and redacts each row to id+time before it leaves the backend. We project the
 * already-redacted rows to `ProviderWorklistRow` so the UI can only ever show
 * patient id + encounter time. Rows arrive sorted by `dos_iso` desc by default.
 */
export async function listMyEncounters(opts: {
  limit?: number;
  offset?: number;
} = {}): Promise<ProviderEncounterList> {
  const { data } = await apiClient.get<ProviderCasesResponse>(
    PROVIDER_ENDPOINTS.listMyEncounters,
    {
      params: withProviderParam({
        sort_by: "dos_iso",
        direction: "desc",
        limit: opts.limit ?? 200,
        offset: opts.offset ?? 0,
      }),
    },
  );
  const rows = data?.cases ?? [];
  const encounters = rows.map(toWorklistRow);
  return {
    total: typeof data?.total === "number" ? data.total : encounters.length,
    encounters,
    windowDays: data?.window_days ?? PROVIDER_LIST_WINDOW_DAYS,
    windowCutoffIso: data?.window_cutoff_iso ?? null,
    unbilledCount: data?.unbilled_count,
    sourceFreshnessState: data?.source_freshness_state,
    coverageState: data?.coverage_state,
  };
}

// ── Next unworked encounter (queue iterator) ───────────────────────────────
export interface ProviderNextCase {
  case: ProviderEncounter | null;
  remaining: number;
}

/**
 * The single highest-priority un-resolved, upbill-eligible encounter for the
 * caller. Pass `afterEncounterId` = the one you just worked to walk forward.
 * Empty queue → `{ case: null, remaining: 0 }`. No provider param.
 */
export async function getNextCase(
  afterEncounterId?: string,
): Promise<ProviderNextCase> {
  const { data } = await apiClient.get(PROVIDER_ENDPOINTS.nextCase, {
    params: afterEncounterId
      ? { after_encounter_id: afterEncounterId }
      : undefined,
  });
  return {
    case: data?.case ?? null,
    remaining: typeof data?.remaining === "number" ? data.remaining : 0,
  };
}

// ── One encounter + lines + evidence (the SINGLE detail endpoint) ───────────
/**
 * The provider's own encounter detail via the SINGLE, server-scoped endpoint
 * (PROVIDER_WEB_CONTRACT §3). The server returns the bakeoff ROW MINUS the
 * coder columns (flat), ENRICHED with `html_refs` + the bucketed
 * `billing_data` / `line_preview`. We normalize the flat row into a `case`
 * object so the detail UI keeps reading `detail.case.*`. NO provider param is
 * sent — the endpoint has no identity param; scope is forced server-side and
 * another provider's id returns 404.
 */
export async function getMyEncounter(
  encounterId: string,
): Promise<ProviderEncounterDetail> {
  const { data } = await apiClient.get<Record<string, unknown>>(
    PROVIDER_ENDPOINTS.getEncounter(encounterId),
    { params: withProviderParam() },
  );
  const row = data ?? {};
  const billingData = (row.billing_data ?? {
    emCode: null,
    labCodes: [],
    questionnaireCodes: [],
    additionalServices: [],
    cptIICodes: [],
  }) as RuBillingData;
  return {
    // Normalize the flat bakeoff-minus-coder row into the `case` shape the
    // detail UI consumes (the coder fields are simply absent server-side).
    case: row as ProviderEncounterDetail["case"],
    html_refs: row.html_refs as RuCaseHtmlRefs | undefined,
    billing_data: billingData,
    line_preview: (row.line_preview ?? []) as RuBillingPreviewRow[],
    charge_justifications: (row.charge_justifications ??
      []) as ProviderChargeJustification[],
    assessment_plan: row.assessment_plan as RuAssessmentPlan | undefined,
  };
}

/** CPT -> ICD-10 match + per-charge dx-pointer table for inline dx editing. */
export async function getMyDxMatch(
  encounterId: string,
  topN = 4,
): Promise<ProviderDxMatch> {
  return fetchDxMatch(encounterId, topN);
}

// ── Durable dx-pointer write (set-pointers human event) ─────────────────────
export async function setMyDxPointers(
  encounterId: string,
  body: { cpt: string; icd10_list: string[]; instance_idx?: number },
): Promise<RuSetDxPointersResult> {
  return revenueSetDxPointers(encounterId, body);
}

// ── Write out / submit the provider's edits ─────────────────────────────────
/**
 * Persist the provider's finished line edits via the SAME durable write path
 * the biller app uses (write-single). The backend asserts the encounter is the
 * caller's (404 otherwise). Provider writes target the encounter's ACTUAL
 * appointment id (decision 2026-06-30): the default is `testAccount: false`,
 * so a physician's edits land on the real encounter in Athena — NOT the
 * practice's sandbox test appointment. (Callers may still pass
 * `testAccount: true` to opt into the sandbox.)
 */
export async function writeOutMyEncounter(
  encounterId: string,
  opts: { charges: ProviderQueueLine[]; testAccount?: boolean } = { charges: [] },
): Promise<RuAthenaWriteSingleResponse> {
  return writeSingleClaimToAthena(encounterId, {
    testAccount: opts.testAccount ?? false,
    charges: opts.charges,
  });
}

// ── Write-out status (poll until the async RPA write resolves) ──────────────
/**
 * One RPA write-out callback event for an encounter's appointment, as surfaced
 * by the provider-scoped status endpoint. The RPA `/billing` write is async: it
 * returns `in_progress` immediately, then POSTs `billing_initiated` /
 * `billing_done` / `billing_failed` callbacks. We poll the status endpoint and
 * read the LATEST event to drive the terminal UI state.
 */
export interface ProviderWriteStatusEvent {
  patientId: string | null;
  appointmentId: string | null;
  batchId: string | null;
  status: string;
  error: string | null;
  timestamp: string | null;
  received_at?: string | null;
}

export interface ProviderWriteStatus {
  encounter_id: string;
  events: ProviderWriteStatusEvent[];
}

/**
 * Poll the recent RPA write-out callbacks for the caller's OWN encounter. The
 * endpoint is provider-gated server-side (404 on cross-provider access) and
 * keys events on the encounter's appointment id, so no PHI / no patient
 * appointment leaks. Events arrive newest-first.
 */
export async function getMyEncounterWriteStatus(
  encounterId: string,
): Promise<ProviderWriteStatus> {
  const { data } = await apiClient.get<{
    encounter_id?: string;
    events?: ProviderWriteStatusEvent[];
  }>(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/status`,
  );
  return {
    encounter_id: data?.encounter_id ?? encounterId,
    events: Array.isArray(data?.events) ? data!.events! : [],
  };
}

/**
 * Park the provider's edits on the day's write queue instead of writing now.
 * Like {@link writeOutMyEncounter}, this defaults to the encounter's REAL
 * appointment (`testAccount: false`). NOTE: not currently wired to a provider
 * button — kept for parity with the write path.
 */
export async function queueMyEncounter(
  encounterId: string,
  opts: { charges: ProviderQueueLine[]; testAccount?: boolean } = { charges: [] },
): Promise<{ queued: boolean; id: string }> {
  return queueClaimForAthena(encounterId, {
    testAccount: opts.testAccount ?? false,
    charges: opts.charges,
  });
}

// ── Auto-save the edited charge set (durable overlay; no write / no queue) ──
/**
 * Persist the provider's CURRENT edited charge set onto the durable bakeoff
 * human-events overlay so add / delete / modifier / units edits SURVIVE a
 * reload and feed write-out — WITHOUT pushing to the RPA or parking a
 * write-queue row. This is the lightweight auto-save the editor debounces on
 * each mutation. Uses the SAME `record_edit_events` overlay (keyed by the
 * stable appointment_anchor) the write/queue paths persist, so provider and
 * biller edits converge on one authoritative store. Provider-gated server-side
 * (404 on a cross-provider encounter); the app sends NO provider param.
 */
export async function persistMyEncounterLines(
  encounterId: string,
  opts: { charges: ProviderQueueLine[] },
): Promise<{ persisted: boolean; encounter_id: string; n_lines: number }> {
  return persistClaimEdit(encounterId, { charges: opts.charges });
}

// ── Execute Billing ──────────────────────────────────────────────────────────

export interface ExecuteBillingResponse {
  job_id: string;
  status: string;
  patient_id: string;
  practice: string;
  out_dir: string;
}

export interface ProviderJobStatus {
  id: string;
  kind: string;
  label: string;
  status: string;
  running: boolean;
  done: boolean;
  exit_code: number | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  error: string | null;
  /**
   * Machine-readable failure classification (set by the backend on failure).
   * `reason_code` names a KNOWN, specific failure shape (e.g.
   * "no_cached_extraction" — a Skip-scraper / rules-only run against an
   * encounter that was never extracted). `retryable` is false when re-running
   * the SAME job would deterministically fail again, so the UI hides Retry and
   * shows the friendly `error` copy. Both default to the transient/unknown
   * case (null / true) — older backends that don't send them are treated as
   * retryable.
   */
  reason_code?: string | null;
  retryable?: boolean;
  result_encounter_id?: string | null;
}

/**
 * Kick off a patient-scoped scrape + pipeline run for a single patient.
 *
 * @param force When true, request a NARROW per-encounter "force re-code": the
 *   backend re-scrapes + re-runs LLM + rules for THIS appt_id even if it was
 *   already coded (via the pipeline's --force-appts/--force-ids flags), instead
 *   of the cache-first skip. Scoped to the clicked encounter — never a
 *   whole-corpus re-code. Requires apptId to take effect.
 * @param skipScrape When true, DO NOT hit the remote scraper: re-code THIS
 *   encounter against its already-scraped cached data (rules-only re-code —
 *   reuses the cached LLM extraction, re-runs only the deterministic rules +
 *   publish). Requires apptId. Backend precedence: skipScrape WINS over force
 *   (they are contradictory — reuse-extraction vs re-extract).
 */
export async function executeProviderBilling(
  patientId: string,
  weeks = 1,
  apptId?: string | null,
  force = false,
  skipScrape = false,
): Promise<ExecuteBillingResponse> {
  const { data } = await apiClient.post<ExecuteBillingResponse>(
    "/revenue/provider/execute-billing",
    {
      patient_id: patientId,
      weeks,
      // The encounter the provider actually clicked "Bill" on. When the
      // patient's encounters are already in the corpus (the common cache-hit
      // case) the scrape produces no new rows and the pipeline is skipped —
      // the backend then falls back to THIS appt_id to write the coded marker,
      // so the clicked card reliably flips to "Coded".
      ...(apptId ? { appt_id: apptId } : {}),
      force,
      // "Skip scraper read": re-code THIS encounter against its already-scraped
      // cached data (rules-only re-code — no remote scrape, no LLM re-extract).
      // Backend precedence: skip_scrape WINS over force.
      skip_scrape: skipScrape,
    },
    // Thread the provider_admin's selected sub-account (?provider=). A no-op
    // for a normal provider. Without it the backend 400s a provider_admin
    // ("must choose a sub-account").
    { params: withProviderParam() },
  );
  return data;
}

/** One per-encounter result from the batch coding endpoint. */
export interface ExecuteBillingBatchResult {
  encounter_id: string;
  patient_id: string | null;
  /** The submitted pipeline job id (null when the id was skipped / errored). */
  job_id: string | null;
  status: string;
  error: string | null;
}

export interface ExecuteBillingBatchResponse {
  requested: number;
  started: number;
  results: ExecuteBillingBatchResult[];
}

/**
 * Run the coding pipeline for MULTIPLE selected encounters in one call.
 *
 * The backend reuses the single-encounter Bill logic for each id and submits
 * one job per encounter onto its single-worker queue (which serializes the
 * runs safely — pipeline stages share corpus files and must not run
 * concurrently). Returns one result per requested id with its job id, which the
 * caller polls exactly like the single flow (`pollProviderJob`). See the
 * backend `provider_execute_billing_batch` for the full parallelism rationale.
 */
export async function executeProviderBillingBatch(
  encounterIds: string[],
  force = false,
  skipScrape = false,
): Promise<ExecuteBillingBatchResponse> {
  const { data } = await apiClient.post<ExecuteBillingBatchResponse>(
    "/revenue/provider/execute-billing-batch",
    { encounter_ids: encounterIds, force, skip_scrape: skipScrape },
    // Thread the provider_admin's selected sub-account (?provider=); a no-op
    // for a normal provider, required for a provider_admin.
    { params: withProviderParam() },
  );
  return data;
}

/** One per-encounter result from the PARALLEL recode endpoint. */
export interface ExecuteBillingParallelResult {
  encounter_id: string;
  patient_id: string | null;
  /** The submitted pipeline job id (null when skipped / out-of-scope / errored). */
  job_id: string | null;
  /**
   * `queued` (a coding job was submitted), `out_of_scope` (missing from the
   * practice snapshot OR owned by a different provider than the selected
   * sub-account — indistinguishable by design), `skipped`, or `error`.
   */
  status: string | null;
  error: string | null;
  /** Existing-billing prefetch outcome (null when bypassed / not run). */
  prefetch?: unknown;
}

export interface ExecuteBillingParallelResponse {
  requested: number;
  started: number;
  /** Actual size of the bounded gets pool used (0 when the prefetch was skipped). */
  max_parallel_gets: number;
  skip_existing_billing: boolean;
  results: ExecuteBillingParallelResult[];
}

/**
 * Bulk unbilled→coded recode (Dr. Basmaa's paste-IDs tool) — PARALLEL gets,
 * SERIALIZED coding.
 *
 * The backend fans the read-only existing-billing HTML "gets" out through a
 * bounded pool (≤8) and then submits ONE coding job per id onto its
 * single-worker queue (pipeline stages mutate shared corpus files and must
 * never run concurrently). Each id is constrained SERVER-SIDE to the caller's
 * own in-practice, in-scope encounters; out-of-scope ids come back as
 * `out_of_scope` (never dispatched). Poll each returned `job_id` with
 * `pollProviderJob`, exactly like the single/batch flows.
 *
 * @param skipExistingBilling When true, BYPASS the existing-billing HTML
 *   prefetch entirely (code fresh from the note) — no gets are issued.
 */
export async function executeProviderBillingParallel(
  encounterIds: string[],
  opts?: { force?: boolean; skipScrape?: boolean; skipExistingBilling?: boolean },
): Promise<ExecuteBillingParallelResponse> {
  const { data } = await apiClient.post<ExecuteBillingParallelResponse>(
    "/revenue/provider/execute-billing-parallel",
    {
      encounter_ids: encounterIds,
      force: opts?.force ?? false,
      skip_scrape: opts?.skipScrape ?? false,
      skip_existing_billing: opts?.skipExistingBilling ?? false,
    },
    // Thread the provider_admin's selected sub-account (?provider=); required
    // for a provider_admin (the server 400s without it).
    { params: withProviderParam() },
  );
  return data;
}

/** Poll job status (includes result_encounter_id when pipeline finishes). */
export async function pollProviderJob(
  jobId: string,
): Promise<ProviderJobStatus> {
  const { data } = await apiClient.get<{ job: ProviderJobStatus }>(
    `/revenue/provider/jobs/${encodeURIComponent(jobId)}`,
  );
  return data.job;
}

/** Fetch the live log tail for a job (returns plain text). */
export async function getProviderJobLog(jobId: string): Promise<string> {
  const { data } = await apiClient.get<string>(
    `/revenue/provider/jobs/${encodeURIComponent(jobId)}/log`,
    { responseType: "text" },
  );
  return typeof data === "string" ? data : "";
}

/** One entry in the provider billing STAGE-TRANSITION ledger. */
export interface ProviderStageLogEntry {
  ts: string;
  encounter_id: string;
  patient_id: string | null;
  /** Stage the encounter MOVED TO. */
  stage: "not_closed" | "unbilled" | "coding" | "coded" | "billing" | "billed" | "failed";
  detail: string | null;
  job_id: string | null;
  cpt_count: number | null;
  actor: string | null;
  practice?: string | null;
}

/**
 * The durable provider billing stage-transition log (newest-first). One row per
 * Unbilled → Coding → Coded → Billed transition. Optionally scoped to a single
 * encounter. Backs the "logs" surface the provider billing story updates at
 * every stage change.
 */
export async function getProviderStageLog(
  opts: { encounterId?: string; limit?: number } = {},
): Promise<ProviderStageLogEntry[]> {
  const params: Record<string, string | number> = {};
  if (opts.encounterId) params.encounter_id = opts.encounterId;
  if (opts.limit) params.limit = opts.limit;
  const { data } = await apiClient.get<{ entries: ProviderStageLogEntry[] }>(
    "/revenue/provider/stage-log",
    { params },
  );
  return data.entries ?? [];
}

/**
 * Clear (zero out) the billing stage-transition log for the provider the caller
 * is acting as. provider_admin ONLY — removes every row tagged with, or whose
 * encounter belongs to, the currently-selected sub-account. Used to strip
 * testing noise so real runs aren't buried under stale entries. Returns the
 * number of rows removed.
 */
export async function clearProviderStageLog(): Promise<{ removed: number }> {
  const { data } = await apiClient.delete<{ removed: number }>(
    "/revenue/provider/stage-log",
  );
  return { removed: data.removed ?? 0 };
}

// ── Today's appointments (preloaded schedule) ──────────────────────────────
/**
 * One row of the provider's preloaded "today's schedule", projected to the
 * MINIMAL, tenant-safe shape the backend returns (the upstream RPA's PII —
 * phone / dob / insurance / age / gender — is dropped server-side). Just
 * enough for the doctor to recognize the patient + drive the Bill flow.
 */
export interface TodaysAppointment {
  /** Opaque AthenaHealth patient id (string) — null for an unmatched walk-in. */
  patient_id: string | null;
  appointment_id: string | null;
  /** The schedule's date (the response-level date), echoed per row. */
  expected_appointment_date: string | null;
  full_name: string | null;
  /** Wall-clock appointment time, e.g. "10:15 AM". */
  time: string | null;
  /** Visit type, e.g. "Office Visit". */
  type: string | null;
  /** Prep status from Athena (e.g. "ready"), when available. */
  prep_status: string | null;
  is_checked_in: boolean | null;
  /**
   * Derived billing-workflow signal (PII-safe enum, never the raw scraped
   * status): "submitted" = Athena generated the claim / sent it to the payer
   * (terminal, from the durable submitted_markers store — the SAME source the
   * encounters/claim-log list uses); "billed" = claim already created
   * (read-only); "coded" = our pipeline coded it (awaiting write-out);
   * "not_closed" = visit exists but the provider has not signed off yet (the
   * daily sweep materialised the card; the sign-off poller flips it to coded on
   * close); null = not ready yet (visit in progress / not checked in). NOTE:
   * there is NO "ready_to_bill" value anymore — the auto-bill subsystem that
   * produced it was removed. Precedence: submitted > billed > coded > not_closed.
   */
  billing_status: "billed" | "coded" | "submitted" | "not_closed" | null;
}

/**
 * The provider's preloaded today's-appointments payload. On an upstream
 * failure the backend returns a STRUCTURED `error` (HTTP 200, empty list) so
 * the UI can show a soft message and the doctor can still type a patient id —
 * this is NOT thrown.
 */
export interface TodaysAppointmentsResponse {
  /** The schedule date as the RPA reports it ("M/D/YYYY"), or null on error. */
  date: string | null;
  appointments: TodaysAppointment[];
  /** True when the upstream RPA served this from its 5-min cache (fast). */
  cached: boolean;
  /**
   * Where THIS response came from: `"server_cache"` = our durable day-keyed
   * cache (no re-scrape this load), `"upstream"` = a fresh fetch we just
   * stored. Undefined on a soft-failure body.
   */
  source?: "server_cache" | "upstream" | null;
  /** ISO timestamp of when we cached this day's schedule (for the hint). */
  cachedAt?: string | null;
  /** Present ONLY on a soft failure — a user-facing message; list is empty. */
  error?: string | null;
  /**
   * True when the selected provider's schedule genuinely CANNOT be downloaded
   * through this practice's appointments connection (it is pinned to a single
   * provider's Athena session). The list is empty NOT because the provider has
   * no appointments, but because their roster isn't retrievable here — the UI
   * must say so instead of implying "0 appointments today".
   */
  providerUnavailable?: boolean;
  /** Optional explanatory note (accompanies `providerUnavailable` or `stale`). */
  note?: string | null;
  /**
   * True when the rows are NOT the current date: either a pinned demo day
   * (`PROVIDER_DEMO_PINNED_DOS`) or the "most recent day we have" fallback used
   * before today's schedule has been loaded. `date` is always the real date of
   * the rows, so the UI must label the day rather than saying "today".
   */
  stale?: boolean;
}

interface TodaysAppointmentsRaw {
  date?: string | null;
  patients?: TodaysAppointment[];
  cached?: boolean;
  source?: "server_cache" | "upstream" | null;
  cached_at?: string | null;
  error?: string | null;
  provider_unavailable?: boolean;
  note?: string | null;
  stale?: boolean;
}

/**
 * GET the authenticated provider's today's AthenaHealth schedule. The backend
 * resolves the per-practice RPA instance + providerid FROM THE JWT (no
 * practice/provider param is sent — tenant safety, mirroring execute-billing).
 *
 * PERFORMANCE: a cold upstream scrape can take 10–120s (cached <100ms). We use
 * a generous client timeout (210s) and the page loads this ASYNCHRONOUSLY,
 * never blocking login or the rest of the page. Pass `refresh: true` to bypass
 * the upstream 5-min cache (the manual "Refresh" affordance).
 */
export async function fetchTodaysAppointments(
  opts: { refresh?: boolean } = {},
): Promise<TodaysAppointmentsResponse> {
  const { data } = await apiClient.get<TodaysAppointmentsRaw>(
    "/revenue/provider/appointments/today",
    {
      params: opts.refresh ? { refresh: true } : undefined,
      // Generous timeout for a cold scrape (server timeout is ~180s).
      timeout: 210_000,
    },
  );
  return {
    date: data?.date ?? null,
    appointments: Array.isArray(data?.patients) ? data!.patients : [],
    cached: Boolean(data?.cached),
    source: data?.source ?? null,
    cachedAt: data?.cached_at ?? null,
    error: data?.error ?? null,
    providerUnavailable: Boolean(data?.provider_unavailable),
    note: data?.note ?? null,
    // The served day is NOT the current date (a pinned demo day, or the
    // most-recent-day fallback). The header must say so rather than calling it
    // "Today's appointments".
    stale: Boolean(data?.stale),
  };
}

// ── Past-day appointments (the poller's lookback window, read-only) ──────────
/** Per-day status rollup. `uncoded` = rows with NO billing_status at all — the
 *  actionable "fell through the cracks" number the history view exists for. */
export interface AppointmentDayCounts {
  coded: number;
  billed: number;
  submitted: number;
  not_closed: number;
  uncoded: number;
}

/** One calendar day of the provider's roster, as cached by the poller. */
export interface AppointmentHistoryDay {
  /** YYYY-MM-DD (the cache bucket key). */
  date_key: string;
  /** The RPA's display date for that day ("M/D/YYYY"), or null if never fetched. */
  date_label: string | null;
  /**
   * FALSE means we have NO roster on file for this day (the poller never got
   * one) — NOT that the provider had zero appointments. The UI must render
   * these differently: "no schedule on file" vs "no appointments".
   */
  cached: boolean;
  cached_at: string | null;
  total: number;
  counts: AppointmentDayCounts;
  appointments: TodaysAppointment[];
}

export interface AppointmentHistoryResponse {
  /** Newest day first; always exactly `lookback_days` entries. */
  days: AppointmentHistoryDay[];
  lookback_days: number;
  timezone: string;
  providerUnavailable?: boolean;
  note?: string | null;
}

interface AppointmentHistoryRaw {
  days?: AppointmentHistoryDay[];
  lookback_days?: number;
  timezone?: string;
  provider_unavailable?: boolean;
  note?: string | null;
}

/**
 * GET the provider's past-day rosters across the poller's lookback window.
 *
 * READ-ONLY / CACHE-ONLY: the backend serves this from the durable day-keyed
 * appointments cache the Not-Closed poller already refreshes every cycle, and
 * never calls the appointments RPA. So this is FAST (no 10–120s cold scrape)
 * and safe to call on every day-tab switch — hence the normal client timeout
 * rather than the 210s one `fetchTodaysAppointments` needs.
 *
 * Pass `days` to override the window; omit it to follow whatever the poller is
 * configured for (`NOT_CLOSED_PREVIOUS_DAYS`), so the view can't drift out of
 * sync with what the backend actually processes.
 */
export async function fetchAppointmentsHistory(
  opts: { days?: number } = {},
): Promise<AppointmentHistoryResponse> {
  const { data } = await apiClient.get<AppointmentHistoryRaw>(
    "/revenue/provider/appointments/history",
    { params: opts.days ? { days: opts.days } : undefined },
  );
  return {
    days: Array.isArray(data?.days) ? data!.days : [],
    lookback_days: data?.lookback_days ?? 0,
    timezone: data?.timezone ?? "",
    providerUnavailable: Boolean(data?.provider_unavailable),
    note: data?.note ?? null,
  };
}


/**
 * Result of forcing a fresh Athena scrape to re-detect billed appointments.
 * The backend durably marks every appointment Athena now reports as billed
 * ("Charges entered"); those drop out of the Unbilled worklist on the next
 * load. Cards may have been billed OUTSIDE this app (a biller entered charges
 * directly in Athena) — this is how the doctor pulls those in.
 */
export interface RefreshBilledStatusResponse {
  /** Appointments scanned on today's fresh scrape. */
  scanned: number;
  /** How many of those Athena reports as already billed. */
  billed_today: number;
  /**
   * DEPRECATED — always 0. There is no "ready to bill" disposition anymore (the
   * auto-bill subsystem was removed); retained optional only for response-shape
   * back-compat with older backends.
   */
  ready_to_bill?: number;
  /** How many are still in progress / not yet at a billing stage. */
  still_open?: number;
  /** How many were NEWLY marked billed by this refresh (first time seen). */
  newly_marked: number;
  /** Total durable billed markers on file after the refresh. */
  total_billed_markers: number;
  /**
   * FULL worklist scope — the count of EVERY in-window unbilled encounter the
   * refresh re-evaluated (not just today's dashboard rows). This is the "all
   * 186" the doctor expects the toggle to span.
   */
  unbilled_total?: number;
  /** Of the full worklist, how many are now billed (durable / live / written-out). */
  unbilled_billed?: number;
  /** Of the full worklist, how many still need action after the refresh. */
  unbilled_still_open?: number;
  /** Present on an upstream failure (soft error, HTTP 200). */
  error?: string | null;
}

/**
 * Force a fresh Athena scrape and update the durable billed markers. Backs the
 * "Refresh billed status" button. A cold scrape can take 10–120s (server
 * timeout ~180s) so we use a generous client timeout.
 */
export async function refreshBilledStatus(): Promise<RefreshBilledStatusResponse> {
  const { data } = await apiClient.post<RefreshBilledStatusResponse>(
    "/revenue/provider/refresh-billed-status",
    undefined,
    { timeout: 210_000 },
  );
  return data;
}

// ── Sync per-appointment Athena billing status (read-only /billing-status) ───
/** One encounter's outcome from the Athena billing-status sync. */
export interface SyncAthenaStatusRow {
  encounter_id: string;
  /** billed | submitted | unbilled | not_billed | billing_not_available | error */
  status:
    | "billed"
    | "submitted"
    | "unbilled"
    | "not_billed"
    | "billing_not_available"
    | "error";
  /** True when it was already at this status before this check (no new write). */
  already?: boolean;
  /** True when this run REVERSED a stale billed marker (billed -> unbilled). */
  reversed?: boolean;
  /** True when Athena has generated the claim (submitted; vs codes-only billed). */
  claim_created?: boolean;
  /** CPT/procedure codes Athena reports on the billing slip. */
  procedures?: string[];
  /** Checked misc codes (e.g. G2211, 96127). */
  misc?: string[];
  /** Present on a per-encounter error. */
  detail?: string;
}

/** Summary of a billing-status sync run the UI can toast + reconcile. */
export interface SyncAthenaStatusResponse {
  /** Encounters we evaluated (selected or full worklist). */
  checked: number;
  /** How many we actually probed against the RPA (skips known-billed). */
  probed?: number;
  /** Newly flipped to billed (charges entered on the slip) by this run. */
  newly_billed: number;
  /** Newly flipped to submitted (claim generated / sent to payer) by this run. */
  newly_submitted?: number;
  /** Newly REVERSED from billed/submitted back to unbilled/coded (no bill in Athena). */
  newly_unbilled: number;
  /** Already billed (no change). */
  already_billed: number;
  /** Already submitted (no change). */
  already_submitted?: number;
  /** Athena billing open but no procedure codes yet. */
  not_billed: number;
  /** Appointment hasn't reached the billing stage in Athena. */
  billing_not_available: number;
  /** Per-encounter check failures. */
  errors: number;
  /** Per-encounter breakdown. */
  results: SyncAthenaStatusRow[];
  /** Present on an upstream transport/config failure (soft error). */
  error?: string | null;
}

/**
 * Read Athena's per-appointment billing status and SYNC our worklist. For every
 * selected encounter (or the whole "needs action" worklist when
 * `allVisible` is true) this calls the read-only `/billing-status` RPA and
 * durably marks billed any encounter Athena reports as billed (a generated
 * claim OR procedure codes on the slip), so it drops out of "needs action".
 * Nothing is written to Athena.
 *
 * The RPA checks appointments SEQUENTIALLY (~8–16s each), so a full-worklist
 * sweep can take minutes — we use a 30-minute client timeout for `allVisible`,
 * and a bounded timeout for a small selection.
 */
export async function syncAthenaStatus(args: {
  encounterIds?: string[];
  allVisible?: boolean;
}): Promise<SyncAthenaStatusResponse> {
  const ids = args.encounterIds ?? [];
  const allVisible = Boolean(args.allVisible);
  // 30 min for a full sweep; ~30s/appt (min 60s) for a bounded selection.
  const timeout = allVisible
    ? 1_800_000
    : Math.min(1_800_000, Math.max(60_000, ids.length * 30_000));
  const { data } = await apiClient.post<SyncAthenaStatusResponse>(
    "/revenue/provider/sync-athena-status",
    { encounter_ids: ids, all_visible: allVisible },
    { timeout },
  );
  return data;
}

// NOTE (2026-07-10 consolidation): the ``billClosedToday`` client + its
// ``BillClosedTodayResponse`` type (the on-demand trigger for the removed
// auto-bill-on-close sweep) were deleted along with the rest of the auto-bill
// subsystem. The Not-Closed sign-off poller now codes each encounter once the
// provider signs off; there is no bulk "bill today's closed" action.

// ── Provider claim-status log ────────────────────────────────────────────────
export type ProviderClaimStatus = "pending" | "reviewed" | "submitted";

/**
 * One row of the provider claim-status log — every claim processed through this
 * interface plus any submitted outside SensAI. Carries the four spec timestamps
 * (sign-off, review-complete, submission) + submitter + status, and an
 * `age_hours` / `is_stale` pair so the UI colors claims older than 24 h.
 */
export interface ProviderClaimLogRow {
  id: string | null;
  source: string | null;
  external: boolean;
  encounter_id: string | null;
  claim_id: string | null;
  patient_id: string | null;
  date_of_service: string | null;
  primary_payer_name: string | null;
  visit_type: string | null;
  summary: { n_lines: number; cpts: string[]; n_units: number };
  signoff_at: number | null;
  queued_at: number | null;
  review_complete_at: number | null;
  reviewed_by: string | null;
  submitted_at: number | null;
  submitted_by: string | null;
  submission_status: number | null;
  submission_error: string | null;
  claim_status: ProviderClaimStatus;
  /** Hours since the most meaningful timestamp; null when unknown. */
  age_hours: number | null;
  /** True when the claim is older than 24 h (drives the stale coloring). */
  is_stale: boolean;
}

export interface ProviderClaimsStatusResponse {
  entries: ProviderClaimLogRow[];
  stats: { pending: number; reviewed: number; submitted: number; external: number };
  /** Epoch seconds when the backend computed this snapshot (the "as of" time). */
  checked_at: number;
}

/**
 * The provider claim-status log — backs the /provider/claim-log page and its
 * "Get status of all claims" button. `search` filters over encounter/claim/
 * patient id, payer, and submitter (server-side).
 */
export async function fetchProviderClaimsStatus(
  opts: { search?: string; limit?: number } = {},
): Promise<ProviderClaimsStatusResponse> {
  const params: Record<string, string | number> = {};
  if (opts.search) params.search = opts.search;
  if (opts.limit) params.limit = opts.limit;
  const { data } = await apiClient.get<ProviderClaimsStatusResponse>(
    "/revenue/provider/claims/status",
    { params },
  );
  return data;
}

// ── Provider CLAIM LOG (submitted / billed claims) ──────────────────────────
// Priority #2: "show submitted claims". Backed by GET /revenue/provider/claim-log,
// assembled from the durable stage-log + submitted/billed markers (the source
// of truth that actually has submitted claims), enriched with the redacted
// patient label / DOS / claim id from the snapshot.
export type ProviderClaimLogStatus = "submitted" | "billed" | "pending";

/** One claim in the provider claim log (one row per encounter, newest-first). */
export interface ProviderClaimLogEntry {
  encounter_id: string;
  patient_id: string | null;
  patient_label: string | null;
  date_of_service: string | null;
  claim_id: string | null;
  provider_display: string | null;
  /**
   * Substantive CPT/HCPCS codes that went out on this claim (E/M, G2211,
   * procedures, labs). Category-II ($0 quality) codes are filtered out of this
   * display list by the backend claim-log assembly.
   */
  cpts: string[];
  n_cpts: number;
  /** Authoritative current status (markers win over the historical event). */
  status: ProviderClaimLogStatus;
  /** The stage event that produced this row ("submitted" | "billed"). */
  stage_event: string;
  detail: string | null;
  actor: string | null;
  /** ISO timestamp of the claim event. */
  ts: string | null;
  /** Epoch seconds for the same timestamp (UI formatting). */
  ts_epoch: number | null;
  /** When the encounter was signed off / closed (ISO), or null if unrecorded. */
  sign_off_at: string | null;
  /** Epoch seconds for the sign-off time. */
  sign_off_epoch: number | null;
  /** When the encounter was coded (ISO), or null if unrecorded. */
  coded_at: string | null;
  /** Epoch seconds for the coded time. */
  coded_epoch: number | null;
  /** When the claim was submitted / billed to Athena (ISO), or null. */
  submitted_at: string | null;
  /** Epoch seconds for the submitted time. */
  submitted_epoch: number | null;
  /**
   * The 835/ERA adjudication outcome for this claim (paid / denied / adjusted,
   * CARC/RARC reason codes, collected $), joined by claim id, or null when no
   * remit has been ingested yet (the row renders a graceful no-remit state).
   */
  adjudication: RuEraAdjudication | null;
}

export interface ProviderClaimLogResponse {
  practice: string;
  entries: ProviderClaimLogEntry[];
  stats: {
    submitted: number;
    billed: number;
    pending: number;
    total: number;
    /** Number of claims with an ingested 835/ERA remit. */
    adjudicated?: number;
    /** Per-outcome counts across adjudicated claims. */
    paid?: number;
    denied?: number;
    adjusted?: number;
    /** Total MEASURED collected dollars across adjudicated claims. */
    collected_total?: number;
  };
  checked_at: number;
}

/**
 * The provider CLAIM LOG — every claim written / submitted to Athena, newest
 * first, with its current status and the codes that went out. Backs the
 * /provider/claims page.
 */
export async function fetchProviderClaimLog(
  opts: { limit?: number } = {},
): Promise<ProviderClaimLogResponse> {
  const params: Record<string, string | number> = {};
  if (opts.limit) params.limit = opts.limit;
  const { data } = await apiClient.get<ProviderClaimLogResponse>(
    "/revenue/provider/claim-log",
    { params },
  );
  return data;
}

/**
 * Fetch the Athena billing-summary HTML for a case (GET
 * /revenue/cases/{encounter_id}/billing-summary → HTMLResponse). Returns the
 * raw HTML string. `responseType: "text"` + a passthrough `transformResponse`
 * keep axios from JSON-parsing the body (its default response transform tries
 * JSON.parse and would mangle the HTML). Non-2xx rejects, so the drawer can
 * render an error state.
 */
export async function fetchCaseBillingSummary(
  encounterId: string,
): Promise<string> {
  const { data } = await apiClient.get<string>(
    `/revenue/cases/${encodeURIComponent(encounterId)}/billing-summary`,
    { responseType: "text", transformResponse: [(d) => d] },
  );
  return typeof data === "string" ? data : String(data ?? "");
}

// ── Provider-admin (Dr. Basmaa) super-user surfaces ─────────────────────────

/** One selectable sub-account for the provider_admin dropdown. */
export interface AvailableProvider {
  provider_key: string;
  display_name: string;
  n_encounters: number;
}

/**
 * The providers Dr. Basmaa may act as — bounded to the DISTINCT rendering
 * providers present in HER practice snapshot (server-side). Backs the
 * sub-account dropdown. 403 for a non-provider_admin caller.
 */
export async function fetchAvailableProviders(): Promise<AvailableProvider[]> {
  const { data } = await apiClient.get<{ providers?: AvailableProvider[] }>(
    "/revenue/provider/available-providers",
  );
  return data?.providers ?? [];
}

/**
 * Move an encounter between the Unbilled, Coded and Billed stages
 * (provider_admin only, gated `billing.stage.move` server-side). "unbilled"
 * is a full reset — it clears the durable coded/billed/submitted markers AND
 * the write-out row so the encounter falls back to Unbilled and can be
 * re-billed; "coded" reverses the billed marker, clears the write-out queue
 * row, and re-marks it coded; "billed" marks it billed. Reflected on the next
 * worklist read (precedence Submitted>Billed>Coded>Unbilled).
 */
export async function moveStage(
  encounterId: string,
  to: "unbilled" | "coded" | "billed",
): Promise<{ encounter_id: string; moved_to: string; practice: string }> {
  const { data } = await apiClient.post<{
    encounter_id: string;
    moved_to: string;
    practice: string;
  }>("/revenue/provider/stage/move", { encounter_id: encounterId, to });
  return data;
}

/** Result of the guarded coded→unbilled revert. */
export interface RevertCodedResponse {
  encounter_id: string;
  moved_to: string;
  practice: string;
  /** True when a coded marker was actually cleared; false for an idempotent no-op. */
  reverted: boolean;
  detail?: string;
}

/**
 * SAFE coded→unbilled revert (provider_admin only) — the reverse path of the
 * paste-IDs recode tool.
 *
 * Unlike `moveStage(id, "unbilled")` (a FULL reset that also strips
 * billed/submitted markers), this enforces CODED-ONLY semantics SERVER-SIDE:
 * a Billed or Submitted encounter is REFUSED with a 409 (a real Athena claim
 * exists — it is never silently stripped); an already-Unbilled encounter is an
 * idempotent no-op. The encounter is constrained to the caller's selected
 * in-practice sub-account (404/not-probeable for anything out of scope).
 */
export async function revertCodedToUnbilled(
  encounterId: string,
): Promise<RevertCodedResponse> {
  const { data } = await apiClient.post<RevertCodedResponse>(
    "/revenue/provider/stage/revert-coded",
    { encounter_id: encounterId },
    { params: withProviderParam() },
  );
  return data;
}

/**
 * Read whether operational Demo Mode is ON (provider_admin super-user only,
 * gated `billing.stage.move` server-side — the SAME gate as Revert Coded).
 *
 * When ON, a backend `--reload` re-spawn SKIPS the heavy startup warm-up so a
 * mid-demo code change doesn't trigger a ~3-min CPU/RAM storm. Pure read — no
 * business logic here; the server owns the flag-file state.
 */
export async function fetchDemoMode(): Promise<boolean> {
  const { data } = await apiClient.get<{ enabled: boolean }>(
    "/revenue/provider/demo-mode",
  );
  return Boolean(data?.enabled);
}

/**
 * Turn Demo Mode ON/OFF (provider_admin super-user only, gated
 * `billing.stage.move`). Creates/removes the backend `.demo_mode` flag file
 * and returns the resulting state. No FE logic — just posts the desired state.
 */
export async function setDemoMode(enabled: boolean): Promise<boolean> {
  const { data } = await apiClient.post<{ enabled: boolean }>(
    "/revenue/provider/demo-mode",
    { enabled },
  );
  return Boolean(data?.enabled);
}

/**
 * Remotely VOID a billed E&M/CPT line in Athena (provider_admin only, gated
 * `billing.line.void` server-side). Implemented as "clear-except-CPT-II then
 * re-bill the surviving lines" — net effect: only this line is gone, Cat-II
 * quality codes are preserved. This drives TWO sequential RPA jobs (~60–120s
 * each), so the request can take a few minutes; we set a generous timeout and
 * the caller shows a staged progress UI. Also records a durable `reject_line`
 * overlay so the deletion survives reload / future writes.
 */
export async function voidLine(
  encounterId: string,
  body: { cpt: string; modifiers?: string[]; units?: number; testAccount?: boolean },
): Promise<{
  ok?: boolean;
  encounter_id: string;
  voided: { cpt: string; modifiers: string[]; units?: number };
  rebilled: string[];
}> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/void-line`,
    {
      cpt: body.cpt,
      modifiers: body.modifiers ?? [],
      units: body.units,
      test_account: body.testAccount ?? false,
    },
    // Two chained RPA jobs (clear + rebill) at 60–120s each. Give server-side
    // polling (ATHENA_RPA_JOB_POLL_TIMEOUT_S) room before the client gives up.
    { timeout: 330_000 },
  );
  return data;
}

/**
 * OVERWRITE the encounter's CPT Category-II (quality) code set in Athena
 * (provider_admin only, gated `billing.line.void`). Clears ALL existing Cat-II
 * codes then adds exactly `codes` (send [] to clear the section). One job-based
 * RPA run (~60–120s). Codes must be NNNNF (e.g. "3008F").
 */
export async function cat2Overwrite(
  encounterId: string,
  codes: string[],
  opts: { testAccount?: boolean } = {},
): Promise<{ encounter_id: string; cat_ii_codes: string[] }> {
  const { data } = await apiClient.post(
    `/revenue/bakeoff/athena/encounter/${encodeURIComponent(encounterId)}/cat2-overwrite`,
    { codes, test_account: opts.testAccount ?? false },
    { timeout: 210_000 },
  );
  return data;
}

/** Provider-facing feature config for the caller's practice. */
export interface ProviderConfig {
  practice: string;
  /** Whether the provider may submit UPLOADED encounters (source=provider_upload). */
  allow_uploaded_submit: boolean;
}

/** Read the caller's practice provider config (allow_uploaded_submit, …). */
export async function fetchProviderConfig(): Promise<ProviderConfig> {
  const { data } = await apiClient.get<ProviderConfig>("/revenue/provider/config");
  return data;
}

// ── Not-Closed poller: runtime Start/Stop/Status ───────────────────────────

/** Today's Not-Closed lifecycle counts, derived from the durable marker store. */
export interface NotClosedTodayCounts {
  n_cards: number;
  n_pending: number;
  n_coding: number;
  n_closed: number;
}

/** Live runtime status of the Not-Closed sweep+poller daemon. */
export interface NotClosedPollerStatus {
  running: boolean;
  /** Boot-time NOT_CLOSED_ENABLED default (runtime Start does NOT persist across restart). */
  enabled_default: boolean;
  practices: string[];
  poll_interval_seconds: number;
  eod_cutoff_hour: number;
  started_at: string | null;
  last_poll_at: string | null;
  /** Why the poller would/wouldn't tick right now (from should_poll_now). */
  next_reason: string | null;
  today: NotClosedTodayCounts;
}

/**
 * Fetch the live status of the Not-Closed poller. Available to the provider who
 * owns the _global queue (Chotani) and to a provider_admin acting in _global —
 * same trust level as Bill / Resync on this page. `?provider=` is threaded for a
 * provider_admin (no-op for a locked provider).
 */
export async function fetchNotClosedPollerStatus(): Promise<NotClosedPollerStatus> {
  const { data } = await apiClient.get<NotClosedPollerStatus>(
    "/revenue/provider/not-closed/poller/status",
    { params: withProviderParam() },
  );
  return data;
}

/** Start the Not-Closed poller at runtime (idempotent). Returns the new status. */
export async function startNotClosedPoller(): Promise<NotClosedPollerStatus> {
  const { data } = await apiClient.post<NotClosedPollerStatus>(
    "/revenue/provider/not-closed/poller/start",
    undefined,
    { params: withProviderParam() },
  );
  return data;
}

/** Stop the Not-Closed poller at runtime (idempotent). Returns the new status. */
export async function stopNotClosedPoller(): Promise<NotClosedPollerStatus> {
  const { data } = await apiClient.post<NotClosedPollerStatus>(
    "/revenue/provider/not-closed/poller/stop",
    undefined,
    { params: withProviderParam() },
  );
  return data;
}

// ── Not-Closed: per-encounter LIVE lifecycle state (frontend polling) ───────

/**
 * One today's-appointment lifecycle state, as surfaced by the per-encounter
 * status endpoint the frontend polls every few seconds while the poller runs.
 * ``state`` maps the durable Not-Closed markers + coded/billed stores + the
 * ephemeral poll-progress hint to a display state:
 *   unbilled — Not-Closed card, open (provider not signed off)
 *   checking — poll in progress (fetching summary / testing sign-off)
 *   closed   — sign-off detected; scrape+coding starting (transient)
 *   scraping — the scrape→coding pipeline is running for this encounter
 *   coded    — coding finished (this card is now click-through to the encounter)
 */
export type NotClosedEncounterState =
  | "unbilled"
  | "checking"
  | "closed"
  | "scraping"
  | "coded";

export interface NotClosedEncounterRow {
  appointment_id: string;
  patient_id: string | null;
  time: string | null;
  appt_type: string | null;
  state: NotClosedEncounterState;
  /** True when the encounter was ALREADY coded today → shown coded, not re-scraped. */
  skipped_coded: boolean;
}

/** The Eastern first-appointment time-gate (locked EST/EDT). */
export interface NotClosedGate {
  /** True once local Eastern time reached the first appointment (scraping allowed). */
  open: boolean;
  /** "ok" | "before_first_appointment" | "no_appointments_today". */
  reason: string;
  /** Earliest appointment time-of-day (minutes since midnight), or null. */
  first_minutes: number | null;
  /** "Now" in Eastern (minutes since midnight), or null. */
  now_minutes: number | null;
  /** IANA timezone used (America/New_York). */
  tz: string;
}

export interface NotClosedEncountersResponse {
  practice: string;
  date_key: string | null;
  running: boolean;
  gate: NotClosedGate;
  encounters: NotClosedEncounterRow[];
  error?: string | null;
}

/**
 * Fetch the LIVE per-encounter Not-Closed lifecycle for today's schedule. The
 * page polls this every few seconds while the poller runs to re-render each
 * card's state/label. Reads cached appointments server-side (no scrape). Same
 * auth as the poller control endpoints (provider/provider_admin + _global).
 */
export async function fetchNotClosedEncounters(): Promise<NotClosedEncountersResponse> {
  const { data } = await apiClient.get<NotClosedEncountersResponse>(
    "/revenue/provider/not-closed/encounters",
    { params: withProviderParam() },
  );
  return {
    practice: data?.practice ?? "_global",
    date_key: data?.date_key ?? null,
    running: Boolean(data?.running),
    gate: data?.gate ?? {
      open: true,
      reason: "ok",
      first_minutes: null,
      now_minutes: null,
      tz: "America/New_York",
    },
    encounters: Array.isArray(data?.encounters) ? data!.encounters : [],
    error: data?.error ?? null,
  };
}

// ── Write / Write-and-Submit RPA (billing-code entry + claim creation) ──────
//
// Two DISTINCT provider actions on the encounter page, both proxying the
// job-based write-and-submit RPA through the provider-gated backend:
//   • writeEncounter          — billing-ONLY (save codes, NO claim). Gated
//                               server-side; 501 when the service can't do a
//                               billing-only write yet (we surface it as a
//                               clear, non-fatal message, NOT a silent claim).
//   • writeAndSubmitEncounter — full write + validate + create claim.
//   • getWriteAndSubmitStatus — poll a job to completion (all result variants).
// All use the existing withProviderParam pattern (threads ?provider= for a
// provider_admin sub-account; no-op for a normal provider).

/**
 * The Write / Write-and-Submit request payload — the FLAT, verbatim line set
 * the provider is looking at (including unsaved edits). The frontend does ZERO
 * coding logic: it sends the displayed/edited lines exactly as-is and the
 * BACKEND owns ALL bucketing / E-M selection / dx dedupe+order / G2211 hoist
 * (see backend ``app.services.provider_billing_lines.normalize_billing_lines``).
 */
export interface WriteAndSubmitData {
  /** The flat, as-displayed charge lines, sent verbatim (no FE transformation). */
  lines: WriteLine[];
}

/** One flat charge line, exactly as displayed/edited — sent verbatim. */
export interface WriteLine {
  /** The CPT/HCPCS code as displayed. */
  cpt: string;
  /** Per-line modifiers as displayed (backend normalizes). */
  modifiers: string[];
  /** Units as displayed. */
  units: number;
  /** This line's own dx pointers, in display order (backend dedupes/orders). */
  icdCodes: string[];
}

/** Submit response (202 queued) OR the duplicate 200 body. */
export interface WriteAndSubmitJob {
  /** The queued job id to poll — null on a duplicate (already in flight). */
  jobId: string | null;
  appointmentId?: string | null;
  /** "queued" (202), or "in_progress"/"already_queued" on a 200 duplicate. */
  status: string;
  /** Human message present on a duplicate submission. */
  message?: string | null;
}

/** One "procedure" line inside a created claim. */
export interface WriteAndSubmitProcedure {
  code?: string;
  description?: string;
  dx?: string[] | string;
  units?: number | string;
  amount?: number | string;
  [k: string]: unknown;
}

/** The created-claim object (present when a claim was actually created). */
export interface WriteAndSubmitClaim {
  claimId?: string;
  statuses?: {
    primary?: string | null;
    secondary?: string | null;
    patient?: string | null;
  };
  holds?: string[];
  totalCharge?: number | string;
  procedures?: WriteAndSubmitProcedure[];
  diagnoses?: string[];
  patient?: string;
  dateOfService?: string;
  serviceDepartment?: string;
  renderingProvider?: string;
  [k: string]: unknown;
}

/**
 * The ``result`` block on a COMPLETED job. Union of the documented variants —
 * every field optional so the UI can discriminate by presence:
 *   • claim created     → success + claimId + claim{...}, alreadyClaimed:false
 *   • validation blocked → success:true, claimId:null, claim:null,
 *                          billingComplete:true, claimCreated:false,
 *                          claimBlockedReason:"validation_errors",
 *                          validationErrors:[...]
 *   • already claimed    → success:true, skipped:true, reason, alreadyClaimed:true
 */
export interface WriteAndSubmitResult {
  success?: boolean;
  claimId?: string | null;
  claim?: WriteAndSubmitClaim | null;
  billingComplete?: boolean;
  claimCreated?: boolean;
  claimBlockedReason?: string | null;
  validationErrors?: string[];
  skipped?: boolean;
  reason?: string | null;
  alreadyClaimed?: boolean;
  [k: string]: unknown;
}

/** The full status body the poll returns (passthrough of the RPA status). */
export interface WriteAndSubmitStatus {
  jobId: string;
  appointmentId?: string | null;
  /** queued | processing | completed | failed */
  status: "queued" | "processing" | "completed" | "failed" | string;
  createdAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  /** Present when status === "completed". */
  result?: WriteAndSubmitResult | null;
  /**
   * NEW batch / create-claim shape: one entry per appointment, each carrying a
   * nested ``claim``. When present (and ``result`` is absent) the client
   * normalizer projects ``results[0]`` into ``result`` so the renderer, which
   * consumes ``result``/``result.claim``, keeps working unchanged.
   */
  results?: WriteAndSubmitResult[] | null;
  /** Present when status === "failed". */
  error?: string | null;
}

/**
 * BILLING-ONLY write ("Write" button) — enter the codes on the slip WITHOUT
 * creating a claim. Returns the RPA job (poll it like write-and-submit) when
 * the service supports a billing-only write. When it does NOT (the default —
 * the documented RPA only ships the combined route) the backend returns HTTP
 * 501; the caller detects that and shows the honest "billing-only write not
 * supported by the service yet" message rather than creating a claim.
 */
export async function writeEncounter(
  appointmentId: string,
  data: WriteAndSubmitData,
): Promise<WriteAndSubmitJob> {
  const { data: res } = await apiClient.post<WriteAndSubmitJob>(
    PROVIDER_ENDPOINTS.write(appointmentId),
    data,
    { params: withProviderParam() },
  );
  return res;
}

/**
 * FULL write + validate + create claim ("Write and Submit" button). Returns the
 * RPA's ``{jobId, appointmentId, status:"queued"}`` (poll `getWriteAndSubmitStatus`),
 * or the duplicate body ``{jobId:null, status:"in_progress"|"already_queued"}``
 * when a job for this appointment is already in flight (don't start a poll).
 */
export async function writeAndSubmitEncounter(
  appointmentId: string,
  data: WriteAndSubmitData,
): Promise<WriteAndSubmitJob> {
  const { data: res } = await apiClient.post<WriteAndSubmitJob>(
    PROVIDER_ENDPOINTS.writeAndSubmit(appointmentId),
    data,
    { params: withProviderParam() },
  );
  return res;
}

/**
 * Provider "Verify" — advisory existence + claim-correctness checks on the
 * CURRENT (possibly edited) line set, WITHOUT writing. POSTs the SAME flat
 * verbatim line payload the write path uses (reuse `buildWriteAndSubmitData` /
 * the page's `buildData()`) and returns the BACKEND-produced warning map
 * verbatim ({@link RuValidateResult}) — warnings are keyed by code (CPT/HCPCS
 * for procedure warnings, the raw ICD-10 string for dx warnings). The frontend
 * does ZERO coding logic: it only serializes the displayed lines and renders
 * the returned {severity, message} strings. Advisory only — never blocks.
 */
export async function verifyProviderLines(
  appointmentId: string,
  data: WriteAndSubmitData,
): Promise<RuValidateResult> {
  const { data: res } = await apiClient.post<RuValidateResult>(
    PROVIDER_ENDPOINTS.validateLines(appointmentId),
    data,
    { params: withProviderParam() },
  );
  return res;
}

/**
 * A surfaced hold-risk finding on the D2 live preflight advisory. Advisory only.
 */
export interface PreflightFinding {
  rule_id: string | null;
  confidence?: number | null;
  required_action?: string | null;
  assertion?: string | null;
}

/**
 * A D4 safe-normalization decision (per §15.6a predicate gate). `auto_applicable`
 * is true only when EVERY predicate held AND the family is promoted to
 * SAFE_NORMALIZATION. Carries only value HASHES, never raw identifiers.
 */
export interface PreflightSafeNormalization {
  operation: string;
  rule_family?: string;
  segmentation?: string | null;
  predicates: Record<string, boolean>;
  predicate_set_satisfied: boolean;
  auto_applicable: boolean;
  reason: string;
}

/**
 * The D2/D4 live pre-submission assurance PREVIEW (§9.1). Advisory-only — this
 * never blocks or transforms; it shows what assurance would say before submit.
 */
export interface PreflightAdvisory {
  available: boolean;
  response_class: string;
  decision?: string; // PROCEED | FAIL_OPEN
  surfaced_findings?: PreflightFinding[];
  safe_normalizations?: PreflightSafeNormalization[];
  notes?: string[];
  block?: boolean;
  advisory_only?: boolean;
  reason?: string;
}

/**
 * Fetch the D2/D4 live preflight advisory for the EXACT payload the write would
 * send. Advisory-only preview: the backend passes record=false, so a look never
 * persists anything and never writes to Athena. Fails soft (available:false).
 */
export async function fetchPreflightAdvisory(
  appointmentId: string,
  data: WriteAndSubmitData,
): Promise<PreflightAdvisory> {
  const { data: res } = await apiClient.post<PreflightAdvisory>(
    PROVIDER_ENDPOINTS.preflightAdvisory(appointmentId),
    data,
    { params: withProviderParam() },
  );
  return res;
}

/**
 * G8 — one meaning-changing finding surfaced for expert approval. A payload-level
 * transformation vs. the original agent proposal (e.g. an added modifier) that
 * changes clinical/billing meaning and therefore requires an explicit decision
 * before it may be submitted. PHI-free.
 */
export interface ApprovalFinding {
  finding_type?: string;
  rule_id: string | null;
  assertion?: string | null;
  meaning_impact?: string;
  required_action?: string | null;
}

/**
 * G8 §8.2 evaluation of the OUTGOING payload: whether expert approval is required
 * before this exact payload may be written, the payload hash the approval binds
 * to, and the meaning-changing findings driving the requirement.
 */
export interface ApprovalEvaluation {
  available: boolean;
  approval_required: boolean;
  payload_hash?: string;
  payload_version_id?: string;
  findings?: ApprovalFinding[];
  reason?: string;
}

/** Result of recording an expert decision (§9.1). */
export interface ApprovalDecisionResult {
  ok?: boolean;
  approval_required: boolean;
  payload_hash?: string;
  decision?: string;
  findings?: ApprovalFinding[];
  note?: string;
}

/**
 * G8 §8.2 — ask the backend whether the OUTGOING payload requires expert approval
 * (a meaning-changing edit vs. the original agent proposal). Also seeds the
 * AGENT_PROPOSED baseline the submit-time gate diffs against. Fails soft.
 */
export async function evaluateApproval(
  appointmentId: string,
  data: WriteAndSubmitData,
  agentProposed: WriteAndSubmitData,
): Promise<ApprovalEvaluation> {
  const { data: res } = await apiClient.post<ApprovalEvaluation>(
    PROVIDER_ENDPOINTS.approvalEvaluate(appointmentId),
    { ...data, agent_proposed: agentProposed },
    { params: withProviderParam() },
  );
  return res;
}

/**
 * G8 §9.1 — record an expert decision for the EXACT outgoing payload. The
 * approval binds to the payload hash + findings digest the submit gate checks,
 * so it unlocks only this exact payload.
 */
export async function recordApprovalDecision(
  appointmentId: string,
  data: WriteAndSubmitData,
  agentProposed: WriteAndSubmitData,
  decision: "approved" | "rejected" | "changes_requested",
  decisionReason?: string,
): Promise<ApprovalDecisionResult> {
  const { data: res } = await apiClient.post<ApprovalDecisionResult>(
    PROVIDER_ENDPOINTS.approvalDecision(appointmentId),
    { ...data, agent_proposed: agentProposed, decision, decision_reason: decisionReason },
    { params: withProviderParam() },
  );
  return res;
}

/**
 * Poll a write-and-submit job to completion. Returns the full status body
 * (queued → processing → completed|failed) including the `result` variant on
 * completion and `error` on failure. A 404 (job not found / expired) throws —
 * the caller stops polling and shows the error.
 */
export async function getWriteAndSubmitStatus(
  jobId: string,
): Promise<WriteAndSubmitStatus> {
  const { data } = await apiClient.get<WriteAndSubmitStatus & { status?: string }>(
    PROVIDER_ENDPOINTS.writeAndSubmitStatus(jobId),
  );
  // The RPA now returns a BATCH / create-claim shape ({status:"success",
  // results:[{claim:{claimId,…}}]}) alongside the legacy singular shape
  // ({status:"completed", result:{claim:{claimId}}}). Normalize both onto the
  // vocabulary the encounter-page poller + renderer understand, additively so
  // the old shape still works:
  //   • status "success" → "completed" (terminal), mirroring getWriteStatus.
  //   • when the singular ``result`` is absent but ``results[]`` is present,
  //     project ``results[0]`` (including its nested ``claim``) into ``result``
  //     so the renderer, which reads ``result``/``result.claim``, is unchanged.
  const raw = (data?.status ?? "").toString();
  const status =
    raw === "success" || raw === "completed"
      ? "completed"
      : raw === "in-progress" || raw === "processing"
        ? "processing"
        : raw === "failed"
          ? "failed"
          : raw || "queued";
  let result = data?.result ?? null;
  if (!result && Array.isArray(data?.results) && data.results.length > 0) {
    result = data.results[0] ?? null;
  }
  return { ...data, status, result } as WriteAndSubmitStatus;
}

/**
 * Poll a billing-only "Write" job (the ENTER-CHARGES lock step) to completion.
 * "Write" ADDS the codes via /billing then LOCKS via /enter-charges and returns
 * that lock jobId — so it's polled here (GET /enter-charges/:jobId), not via
 * getWriteAndSubmitStatus. The status body walks the same queued→…→success
 * shape; we surface it as a WriteAndSubmitStatus so the encounter page's poller
 * can treat "success" like a completed job. A 404 (unknown/expired) throws.
 */
export async function getWriteStatus(
  jobId: string,
): Promise<WriteAndSubmitStatus> {
  const { data } = await apiClient.get<WriteAndSubmitStatus & { status?: string }>(
    PROVIDER_ENDPOINTS.writeStatus(jobId),
  );
  // The enter-charges job walks queued→in-progress→success|failed. Normalize its
  // terminal/active labels onto the write-and-submit vocabulary the encounter
  // page poller understands (completed/processing) so one poller drives both.
  const raw = (data?.status ?? "").toString();
  const status =
    raw === "success" || raw === "completed"
      ? "completed"
      : raw === "in-progress" || raw === "processing"
        ? "processing"
        : raw === "failed"
          ? "failed"
          : raw || "queued";
  return { ...data, status } as WriteAndSubmitStatus;
}

/**
 * Serialize the CURRENT in-page line-editor state into the Write / Write-and-
 * Submit request payload — the FLAT, verbatim line set the provider is looking
 * at (including any unsaved edits).
 *
 * LOGIC-FREE FRONTEND: this does NO coding logic. It does not bucket codes,
 * select/drop an E/M line, dedupe/reorder dx pointers, or hoist G2211. It emits
 * each displayed line exactly as-is (cpt/modifiers/units + that line's own dx
 * pointers, in display order). The BACKEND
 * (``app.services.provider_billing_lines.normalize_billing_lines``) owns ALL of
 * that bucketing/selection/dedupe/hoist — the single source of truth shared
 * with the write-out path. `dxPointersFor` yields this line's ordered ICD-10
 * pointers; they are passed through untouched (backend does the encounter-level
 * dedupe/order).
 */
export function buildWriteAndSubmitData(
  lines: {
    cpt: string;
    units: number;
    modifiers?: string[];
    _lid: string;
  }[],
  dxPointersFor: (lid: string) => string[],
): WriteAndSubmitData {
  return {
    lines: lines.map((l) => ({
      cpt: l.cpt,
      modifiers: l.modifiers ?? [],
      units: l.units,
      icdCodes: dxPointersFor(l._lid) ?? [],
    })),
  };
}

